'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Message {
  id: string;
  sender: 'user' | 'specialist';
  text: string;
  timestamp: string;
}

export default function LiveChatWidget() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      sender: 'specialist',
      text: 'Hello! I\'m Sarah, a technical specialist. How can I help you with our product suite today?',
      timestamp: '10:30 AM',
    },
  ]);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleSendMessage = () => {
    if (!inputValue.trim() || !isHydrated) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      sender: 'user',
      text: inputValue,
      timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    };

    setMessages([...messages, newMessage]);
    setInputValue('');

    // Mock specialist response
    setTimeout(() => {
      const response: Message = {
        id: (Date.now() + 1).toString(),
        sender: 'specialist',
        text: 'Thank you for your question. Let me connect you with the right specialist who can provide detailed information about that feature.',
        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages(prev => [...prev, response]);
    }, 1500);
  };

  if (!isHydrated) {
    return null;
  }

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-16 h-16 bg-conversion text-conversion-foreground rounded-full shadow-cta hover:scale-110 transition-all duration-300 z-50 flex items-center justify-center"
          aria-label="Open live chat"
        >
          <Icon name="ChatBubbleLeftRightIcon" size={28} variant="solid" />
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-success rounded-full border-2 border-white"></div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[32rem] bg-card rounded-lg shadow-2xl border border-border z-50 flex flex-col overflow-hidden animate-slide-in">
          {/* Header */}
          <div className="bg-primary text-primary-foreground p-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center">
                  <Icon name="UserIcon" size={20} variant="solid" />
                </div>
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-success rounded-full border-2 border-primary"></div>
              </div>
              <div>
                <p className="font-display font-semibold text-sm">Technical Support</p>
                <p className="font-body text-xs opacity-80">Online - Avg response: 2 min</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors duration-300"
            >
              <Icon name="XMarkIcon" size={20} variant="solid" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-surface">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[75%] rounded-lg p-3 ${
                    message.sender === 'user' ?'bg-primary text-primary-foreground' :'bg-card text-foreground border border-border'
                  }`}
                >
                  <p className="font-body text-sm">{message.text}</p>
                  <p className={`font-body text-xs mt-1 ${
                    message.sender === 'user' ? 'text-primary-foreground opacity-70' : 'text-muted-foreground'
                  }`}>
                    {message.timestamp}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="px-4 py-2 bg-card border-t border-border">
            <div className="flex flex-wrap gap-2">
              <button className="px-3 py-1.5 bg-surface text-foreground rounded-full font-body text-xs hover:bg-muted transition-colors duration-300">
                Schedule Demo
              </button>
              <button className="px-3 py-1.5 bg-surface text-foreground rounded-full font-body text-xs hover:bg-muted transition-colors duration-300">
                API Documentation
              </button>
              <button className="px-3 py-1.5 bg-surface text-foreground rounded-full font-body text-xs hover:bg-muted transition-colors duration-300">
                Pricing Info
              </button>
            </div>
          </div>

          {/* Input */}
          <div className="p-4 bg-card border-t border-border">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 bg-surface border border-border rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim()}
                className="px-4 py-2 bg-conversion text-conversion-foreground rounded-md hover:shadow-cta transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name="PaperAirplaneIcon" size={20} variant="solid" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}